import axios, {parseResponseData} from '@/axiosInstance'
import {captureException} from '@sentry/react'
import {ProjectBudgets, ExternalCost, AccessRate, ExternalCostCode} from '../types'
import {PaginationParams, PaginationResponse} from '@/types'
import {z} from 'zod'

export const httpGetProjectBudgets = async () => {
    const response = await axios.get('projects-budgets')
    return parseResponseData({
        responseData: response.data,
        responseShape: ProjectBudgets.array(),
        onZodError: captureException
    })
}

export interface HttpStoreProjectBudgetOptions {
    payload: {
        year: number
        outpatientAmount: number
        houseAmount: number
    }
}
export const httpStoreProjectBudget = async ({payload}: HttpStoreProjectBudgetOptions) => {
    const response = await axios.post('projects-budgets', payload)
    return parseResponseData({
        responseData: response.data,
        responseShape: ProjectBudgets,
        onZodError: captureException
    })
}

export interface HttpUpdateProjectBudgetOptions {
    urlParams: {
        id: string
    }
    payload: {
        year: number
        houseAmount: number
        outpatientAmount: number
    }
}
export const httpUpdateProjectBudget = async ({urlParams, payload}: HttpUpdateProjectBudgetOptions) => {
    const response = await axios.patch(`projects-budgets/${urlParams.id}`, payload)
    return parseResponseData({
        responseData: response.data,
        responseShape: ProjectBudgets,
        onZodError: captureException
    })
}

export interface HttpDeleteProjectBudgetOptions {
    urlParams: {
        id: string
    }
}
export const httpDeleteProjectBudget = async ({urlParams}: HttpDeleteProjectBudgetOptions) => {
    await axios.delete(`projects-budgets/${urlParams.id}`)
}

export interface HttpGetExternalCostsOptions {
    params: PaginationParams & {
        year: number
    }
}
export const HttpGetExternalCostsResponse = z.object({
    data: ExternalCost.array(),
    meta: PaginationResponse
})
export type HttpGetExternalCostsResponse = z.infer<typeof HttpGetExternalCostsResponse>
export const httpGetExternalCosts = async ({params}: HttpGetExternalCostsOptions) => {
    const response = await axios.get('external-costs', {params})
    return parseResponseData({
        responseData: response.data,
        responseShape: HttpGetExternalCostsResponse,
        onZodError: captureException
    })
}

export interface HttpStoreExternalCostOptions {
    payload: {
        description: string
        amount: number
        type: 'yearly' | 'monthly'
        year: number
        month: number
        untilMonth?: number
        untilYear?: number
    }
}
export const httpStoreExternalCost = async ({payload}: HttpStoreExternalCostOptions) => {
    const response = await axios.post('external-costs', payload)
    return parseResponseData({
        responseData: response.data,
        responseShape: ExternalCost,
        onZodError: captureException
    })
}

export interface HttpUpdateExternalCostOptions {
    urlParams: {
        id: string
    }
    payload: {
        description: string
        amount: number
        type: 'yearly' | 'monthly'
        year: number
        month: number
        untilMonth?: number
        untilYear?: number
    }
}
export const httpUpdateExternalCost = async ({urlParams, payload}: HttpUpdateExternalCostOptions) => {
    const response = await axios.patch(`external-costs/${urlParams.id}`, payload)
    return parseResponseData({
        responseData: response.data,
        responseShape: ExternalCost,
        onZodError: captureException
    })
}

export interface HttpDeleteExternalCostOptions {
    urlParams: {
        id: string
    }
}
export const httpDeleteExternalCost = async ({urlParams}: HttpDeleteExternalCostOptions) => {
    await axios.delete(`external-costs/${urlParams.id}`)
}

export interface HttpGetAccessRatesOptions {
    params: PaginationParams & {
        code: ExternalCostCode
    }
}
export const HttpGetAccessRatesResponse = z.object({
    data: AccessRate.array(),
    meta: PaginationResponse
})
export type HttpGetAccessRatesResponse = z.infer<typeof HttpGetAccessRatesResponse>
export const httpGetAccessRates = async ({params}: HttpGetAccessRatesOptions) => {
    const response = await axios.get('access-rates', {params})
    return parseResponseData({
        responseData: response.data,
        responseShape: HttpGetAccessRatesResponse,
        onZodError: captureException
    })
}

export interface HttpUpdateAccessRatesOptions {
    urlParams: {
        id: string
    }
    payload: {
        code: ExternalCostCode
        rate: number
        start: string
        end: string | null
    }
}
export const httpUpdateAccessRates = async ({urlParams, payload}: HttpUpdateAccessRatesOptions) => {
    const response = await axios.patch(`access-rates/${urlParams.id}`, payload)
    return parseResponseData({
        responseData: response.data,
        responseShape: AccessRate,
        onZodError: captureException
    })
}

export interface HttpStoreAccessRatesOptions {
    payload: {
        code: ExternalCostCode
        rate: number
        start: string
        end: string | null
    }
}
export const httpStoreAccessRates = async ({payload}: HttpStoreAccessRatesOptions) => {
    const response = await axios.post('access-rates', payload)
    return parseResponseData({
        responseData: response.data,
        responseShape: AccessRate,
        onZodError: captureException
    })
}
